import { Box, NavbarSearchBox, Theme } from "@outschool/backpack";
import { useTranslation } from "@outschool/localization";
import { searchPath } from "@outschool/routes";
import { useSession } from "@outschool/ui-auth";
import { useMobileSearchBar } from "@outschool/ui-components-website";
import { useIsMobile, useNavigation } from "@outschool/ui-utils";
import React, { Suspense } from "react";
import { useLocation } from "react-router-dom";

import { useActiveLearnerSearchFilters } from "../../hooks/useActiveLearnerSearchFilters";
import useExperiment from "../../hooks/useExperiment";
import { useSearchByLearner } from "../../hooks/useSearchByLearner";
import { SearchFiltersContextProvider } from "../../lib/useSearchFilters";
import { useActiveLearner } from "../../providers/ActiveLearnerProvider";
import { useIsParentDashboardPage } from "../parentDashboardV2/hooks/useDashboardLinks";
import DesktopSearchField from "../search/desktop/DesktopSearchField";
import { LearnerSelectorVariants } from "../search/shared/learnerSelector/LearnerSelectorConstants";

/**
 * Lazy loaded LearnerSelector component. We lazy load this in order to avoid making big increases
 * to the initial page load bundle size because the LearnerSelector component has a few dependencies
 * that are otherwise not needed for the nav bar.
 */
const LearnerSelector = React.lazy(() =>
  import("../search/shared/learnerSelector/LearnerSelector").then(module => ({
    default: module.LearnerSelector,
  })),
);

function useGlobalSearchTeachersExperiment() {
  const { isLoggedIn } = useSession();
  const { variant, trigger } = useExperiment("GlobalSearchTeachers");
  const isTreatment = isLoggedIn && variant === "treatment";
  return {
    isTreatment,
    trigger,
  };
}

export default function GlobalSearchBar({
  showSearchBox,
}: {
  showSearchBox: boolean;
}) {
  const nav = useNavigation();
  const { currentUser } = useSession();
  const hasLearners = (currentUser?.children?.length ?? 0) > 0;
  const isParentDashboardPage = useIsParentDashboardPage();
  const hideLearnerSelector = !hasLearners && isParentDashboardPage;
  const { isTreatment, trigger } = useGlobalSearchTeachersExperiment();

  const getLearnerSearchFilters = useActiveLearnerSearchFilters();
  const { learner, getSearchBarPlaceholder } = useSearchByLearner();
  const { setActiveLearner, activeLearnerSet } = useActiveLearner();

  return (
    <Box
      sx={(theme: Theme) => ({
        width: "100%",
        [theme.breakpoints.down("md")]: {
          display: "none",
        },
        // for Pad Mini 768px
        "@media (min-width: 768px) and (max-width: 768px)": {
          "* > form": {
            padding: 0,
          },
        },
      })}
    >
      {showSearchBox ? (
        <SearchFiltersContextProvider>
          <DesktopSearchField
            searchFilters={{}}
            setImplicitSearchFilters={() => {}}
            setSearchFilters={filters => {
              if (learner && hasQueryFilterOnly(filters)) {
                // should update 'search by learner' state only when the clicked keyword does not contain other filters
                const learnerFilters = getLearnerSearchFilters(learner);
                filters = { ...filters, ...learnerFilters };
              }

              // If we're globally searching with a default "all learners" selected, set the active learner with a temporary lifetime
              // so that the search page will use "all learners" rather than defaulting to the newest learner.
              if (!activeLearnerSet && !learner) {
                // Reset learner with ONE_PAGE_LOAD lifetime so the selection doesn't persist
                setActiveLearner({
                  learnerUid: null,
                  // eslint-disable-next-line i18next/no-literal-string
                  lifetime: "ONE_PAGE_LOAD",
                });
              }

              nav(searchPath(filters));
            }}
            clearDropdowns={() => {}}
            /* eslint-disable i18next/no-literal-string */
            searchSuggestionsToIgnore={isTreatment ? [] : ["teachers"]}
            experimentTrigger={trigger}
            showSearchSuggestions
            SearchBoxComponent={NavbarSearchBox}
            searchPlaceholder={getSearchBarPlaceholder(learner)}
            menuStyleSx={{
              top: "45px",
            }}
            leftContent={
              !hideLearnerSelector && (
                <Suspense fallback={null}>
                  <LearnerSelector
                    variant={LearnerSelectorVariants.TOP_BAR}
                    // eslint-disable-next-line i18next/no-literal-string
                    trackingLocation="global_search_bar"
                  />
                </Suspense>
              )
            }
          />
        </SearchFiltersContextProvider>
      ) : (
        // simple box to keep header sizing the same with/without search
        <Box sx={{ height: 55 }} />
      )}
    </Box>
  );
}

export function MobileSearchBar() {
  const ref = React.useRef<HTMLElement>(null);
  const { showMobileSearchBar, setVerticalOffset } = useMobileSearchBar();
  const isMobile = useIsMobile();
  const location = useLocation();
  const isOnIgnoredPage = ["/search", "/users/login"].includes(
    location.pathname,
  );

  React.useLayoutEffect(() => {
    if (!ref.current) {
      return;
    }

    const { height } = ref.current.getBoundingClientRect();

    setVerticalOffset(height);
  }, [setVerticalOffset]);
  if (!isMobile || isOnIgnoredPage || !showMobileSearchBar) {
    return null;
  }

  return (
    <Box
      ref={ref}
      sx={(theme: Theme) => ({
        width: "100%",
        position: "fixed",
        padding: "5px 10px",
        top: 0,
        left: 0,
        zIndex: 2,
        backgroundColor: theme.palette.common.white,
        borderBottom: `1px solid ${theme.palette.grey[200]}`,
      })}
    >
      <SearchBar />
    </Box>
  );
}

export function SearchBar() {
  const nav = useNavigation();
  const { t } = useTranslation("client\\components\\nav\\GlobalSearchBar");
  return (
    <SearchFiltersContextProvider>
      <DesktopSearchField
        searchFilters={{}}
        setImplicitSearchFilters={() => {}}
        setSearchFilters={filters => {
          nav(searchPath(filters));
        }}
        clearDropdowns={() => {}}
        /* eslint-disable i18next/no-literal-string */
        showSearchSuggestions
        SearchBoxComponent={NavbarSearchBox}
        searchPlaceholder={t("Search online classes and tutors")}
        menuStyleSx={{
          top: "45px",
        }}
      />
    </SearchFiltersContextProvider>
  );
}

function hasQueryFilterOnly(filters: any): boolean {
  if (!filters || typeof filters !== "object") {
    return false;
  }

  return Object.entries(filters).every(([key, value]) => {
    if (key === "q") {
      return true;
    }
    return value === null || value === undefined;
  });
}
